/* Shared delivery for Shopify videos. Preparation never starts a download;
   owners keep markup in templates and request playback only when appropriate. */
(() => {
  if (window.PureVideo) return;
  const libraryUrl = document.currentScript?.dataset.hlsLibrary;
  const players = new WeakMap();
  let libraryPromise;
  function loadAdaptivePlayer() {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (!libraryUrl) return Promise.reject(new Error('Adaptive player unavailable'));
    if (!libraryPromise) libraryPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = libraryUrl;
      script.onload = () => window.Hls ? resolve(window.Hls) : reject(new Error('Adaptive player unavailable'));
      script.onerror = () => reject(new Error('Adaptive player failed to load'));
      document.head.append(script);
    });
    return libraryPromise;
  }
  function prepare(video, { separatePoster = false } = {}) {
    if (!video) return null;
    if (separatePoster) video.removeAttribute('poster');
    if (players.has(video)) return players.get(video);
    const nativePlay = video.play.bind(video);
    const nativePause = video.pause.bind(video);
    const events = new AbortController();
    const adaptive = video.querySelector('source[data-quality="adaptive"]')?.src;
    const mp4s = [...video.querySelectorAll('source[type="video/mp4"]')];
    let hls, initialization, wanted = false, disposed = false, fallback = false;
    // Owners call play explicitly; cloning an autoplay attribute must not race
    // adaptive setup and start a second, fixed-resolution MP4 download.
    video.removeAttribute('autoplay');
    video.preload = 'none';
    const pickFallback = () => {
      const target = (video.clientWidth || window.innerWidth) * Math.min(window.devicePixelRatio || 1, 2);
      const sized = mp4s.filter(source => Number(source.dataset.width) > 0);
      const sorted = [...(sized.length ? sized : mp4s)].sort((a, b) => Number(a.dataset.width) - Number(b.dataset.width));
      return (sorted.find(source => Number(source.dataset.width) >= target) || sorted.at(-1))?.src;
    };
    function useFallback() {
      if (fallback || disposed) return;
      const source = pickFallback();
      if (!source) return;
      const resume = wanted;
      const time = video.currentTime;
      fallback = true;
      hls?.destroy();
      hls = null;
      wanted = resume;
      video.src = source;
      if (time > 0) video.addEventListener('loadedmetadata', () => {
        if (Number.isFinite(video.duration)) video.currentTime = Math.min(time, Math.max(0, video.duration - 0.25));
      }, { once: true, signal: events.signal });
      video.load();
      if (wanted && video.isConnected) nativePlay().catch(() => {});
    }
    async function initialize() {
      if (!adaptive) { useFallback(); return; }
      if (video.canPlayType('application/vnd.apple.mpegurl')) return;
      try {
        const Hls = await loadAdaptivePlayer();
        if (disposed) return;
        if (!Hls.isSupported()) { useFallback(); return; }
        hls = new Hls({ autoStartLoad: false, capLevelToPlayerSize: true, maxBufferLength: 6, maxMaxBufferLength: 12, backBufferLength: 30 });
        hls.on(Hls.Events.ERROR, (_event, data) => { if (data.fatal) useFallback(); });
        hls.attachMedia(video);
        hls.loadSource(adaptive);
      } catch { useFallback(); }
    }
    async function play() {
      wanted = true;
      initialization ||= initialize();
      await initialization;
      if (disposed || !wanted || !video.isConnected) return;
      hls?.startLoad(-1);
      return nativePlay();
    }
    function pause() {
      wanted = false;
      nativePause();
      hls?.stopLoad();
    }
    function destroy() {
      if (disposed) return;
      disposed = true;
      pause();
      events.abort();
      hls?.destroy();
      hls = null;
      video.play = nativePlay;
      video.pause = nativePause;
      players.delete(video);
    }
    const controller = { play, pause, destroy, get hls() { return hls; } };
    players.set(video, controller);
    video.play = play;
    video.pause = pause;
    video.addEventListener('pause', () => { wanted = false; hls?.stopLoad(); }, { signal: events.signal });
    video.addEventListener('play', () => { wanted = true; hls?.startLoad(-1); }, { signal: events.signal });
    if (video.dataset.pureVideoReplay === 'true') {
      const replay = () => {
        if (disposed || !video.isConnected || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        video.currentTime = 0;
        play().catch(() => {});
      };
      video.addEventListener('ended', replay, { signal: events.signal });
      video.addEventListener('timeupdate', () => {
        if (!video.paused && video.duration > 0.5 && video.currentTime >= video.duration - 0.25) replay();
      }, { signal: events.signal });
    }
    return controller;
  }
  function videos(root) {
    return [...(root.matches?.('video[data-pure-shopify-video]') ? [root] : []), ...(root.querySelectorAll?.('video[data-pure-shopify-video]') || [])];
  }
  window.PureVideo = { prepare, loadAdaptivePlayer };
  // Heroes own their initial seek/buffer lifecycle. Other owners prepare before
  // insertion; this scan also covers Shopify-added product media.
  const scan = root => videos(root).forEach(video => { if (!video.closest('pure-video-hero')) prepare(video); });
  scan(document);
  new MutationObserver(records => {
    for (const record of records) {
      for (const node of record.addedNodes) scan(node);
      for (const node of record.removedNodes) for (const video of videos(node)) {
        if (!video.isConnected) players.get(video)?.destroy();
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
